import partition from 'lodash/partition';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { EstimateContext } from './estimate.context';
import { EstimateContextType } from './estimate.types';
import { useDefineLoadEstimateCode, useEstimateLoader } from './hooks';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { pullLocalData, setLocalData } from '~/core/helpers/storageHelper';
import { useRouterTools } from '~/core/hooks/useRouterTools';
import { useAppSelector } from '~/redux/hooks';
import { selectIsSavingEstimate } from '~/redux/slices/estimate';
import {
  NotifierType,
  SavedNotification,
  showNotification,
} from '~legacy/components';

export const EstimateContextProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const isSavingEstimate = useAppSelector(selectIsSavingEstimate);
  const { getSearchParam } = useRouterTools();
  const loadEstimateCodeFromURL = getSearchParam(
    LOCAL_STORAGE_KEY.LOAD_ESTIMATE
  );
  const { loadEstimateCode, removeLoadEstimateCode } =
    useDefineLoadEstimateCode();
  const {
    fetchedEstimate,
    isFetchingEstimate,
    isFetchError,
    resetLoadedFlags,
  } = useEstimateLoader(loadEstimateCode);
  const notificationsShownRef = useRef(false);

  const startNewEstimate = useCallback(() => {
    const url = new URL(window.location.href);

    url.searchParams.delete(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);

    window.location.href = url.toString();
  }, []);

  const switchLoadedEstimateCode = useCallback(
    (code: string) => {
      if (code !== '' && code !== loadEstimateCode) {
        resetLoadedFlags();
        setLocalData(LOCAL_STORAGE_KEY.ESTIMATE_CODE_SWITCH, 'true');
        removeLoadEstimateCode();
        window.location.assign(
          `${window.location.origin}?${LOCAL_STORAGE_KEY.LOAD_ESTIMATE}=${code}`
        );
      }
    },
    [loadEstimateCode, removeLoadEstimateCode, resetLoadedFlags]
  );

  useEffect(() => {
    const loadEstimateCodeFromURL = new URL(
      window.location.href
    ).searchParams.get(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);

    if (!loadEstimateCodeFromURL && !notificationsShownRef.current) {
      notificationsShownRef.current = true;

      const errorMsgs = pullLocalData<SavedNotification>(
        LOCAL_STORAGE_KEY.NOTIFICATIONS,
        (item) => item.notifierType === NotifierType.ERROR
      );

      // Only show one "Estimate Not Found" notification, even if multiple are stored
      const [estimateNotFoundMsgs, otherErrorMsgs] = partition(
        errorMsgs,
        (item) => item.title === 'Estimate Not Found'
      );

      // Process "Estimate Not Found" first (if any), then other errors
      const messagesToShow =
        estimateNotFoundMsgs.length > 0
          ? [estimateNotFoundMsgs[0], ...otherErrorMsgs]
          : otherErrorMsgs;

      messagesToShow.forEach((item, index) => {
        if (!item) {
          return;
        }

        const isEstimateNotFound =
          index === 0 && estimateNotFoundMsgs.length > 0;
        const [notifierType, key] = isEstimateNotFound
          ? [NotifierType.ERROR, 'estimate-not-found-error']
          : [
              item.notifierType,
              `error-${item.title}-${item.estimateNumber}-${index + 1}`,
            ];

        showNotification(
          notifierType,
          item.title,
          item.description,
          item.message,
          item.duration,
          key
        );
      });
    }

    // Reset the ref when a new estimate code is loaded
    if (loadEstimateCodeFromURL) {
      notificationsShownRef.current = false;
    }
  }, [loadEstimateCodeFromURL]);

  const contextValue: EstimateContextType = useMemo(
    () => ({
      fetchedEstimate,
      isFetchError,
      isFetchingEstimate,
      isSavingEstimate,
      loadEstimateCode,
      startNewEstimate,
      removeLoadEstimateCode,
      switchLoadedEstimateCode,
      resetLoadedFlags,
    }),
    [
      fetchedEstimate,
      isFetchError,
      isFetchingEstimate,
      isSavingEstimate,
      loadEstimateCode,
      startNewEstimate,
      removeLoadEstimateCode,
      switchLoadedEstimateCode,
      resetLoadedFlags,
    ]
  );

  return (
    <EstimateContext.Provider value={contextValue}>
      {children}
    </EstimateContext.Provider>
  );
};
